/**
 * @file pending-deposit-modal.tsx
 * @module app/modals/pending-deposit-modal
 * @description Renders the pending deposit modal modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Button } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { useNavigate } from 'react-router-dom';
import CrossSvg from '../../_admin/assets/media/close.png';
import { removeKey, setKey } from '../../Global/history';
import { OutstandingReport, PaymentStatusFullyPaid, ReceiptStatusPending, View } from '../../utils/constants';
import Method from '../../utils/methods';
import { listOutstandingReport } from '../../utils/storeString';
import { useAuth } from '../modules/auth';

interface PendingDriver {
  _id: string;
  driverName: string;
  pendingCashDeposit: number;
}

interface PendingDepositModalProps {
  show: boolean;
  drivers: PendingDriver[];
  onClose: () => void;
}

const PendingDepositModal = ({ show, drivers, onClose }: PendingDepositModalProps) => {
  const { currentUser }: any = useAuth();
  const navigate = useNavigate();

  const handleClose = () => {
    onClose();
  };

  const handleViewDetails = (driver: PendingDriver) => {
    let selectedDriver = {
      value: driver._id,
      label: driver.driverName,
      title: driver.driverName
    }
    setKey(listOutstandingReport.filterByDriver, selectedDriver);
    let selectedReceiptStatus = {
      value: ReceiptStatusPending,
      label: "Pending",
      title: "Pending"
    }
    setKey(listOutstandingReport.filterByBankDepositStatus, selectedReceiptStatus);
    let selectedPaymentStatus = {
      value: PaymentStatusFullyPaid,
      label: "Fully Paid",
      title: "Fully Paid"
    }
    setKey(listOutstandingReport.filterByPaymentStatus, selectedPaymentStatus);
    removeKey(listOutstandingReport.filterStartDate);
    removeKey(listOutstandingReport.filterEndDate);
    removeKey(listOutstandingReport.search);
    // Notify Outstanding Report page (if already mounted) to refresh its filters
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('outstanding-filters-updated'));
    }
    navigate('outstanding-report');
    onClose();
  };

  // If user is not available (e.g., during logout), do not render the modal
  if (!currentUser) {
    return null;
  }

  if (!drivers.length) {
    return null;
  }

  return (
    <>
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName={`modal-dialog-centered ${Method.hasPermission(OutstandingReport, View, currentUser) ? "min-w-lg-700px" : "min-w-lg-500px"}`}
      className="border-r10px"
      contentClassName="p-5"
    >
      <Modal.Header className="border-bottom-0 text-center pb-6 mx-auto">
        <div className="symbol symbol-md-40px symbol-35px close-inner-top cursor-pointer">
          <img
            width={40}
            height={40}
            src={CrossSvg}
            alt="close"
            onClick={handleClose}
          />
        </div>
        <Modal.Title className="fs-30 fw-bolder mw-lg-350px">
          Pending cash deposits
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 border border-r10px">
        <div className="table-responsive">
          <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
            <thead className="">
              <tr className="fs-16 fw-bold text-dark border-bottom h-70px align-middle">
                <th className="">Driver name</th>
                <th className="text-center">Pending cash deposit</th>
                {Method.hasPermission(OutstandingReport, View, currentUser) ? (
                  <th className="text-center"></th>
                ) : (
                  <></>
                )}
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver) => (
                <tr key={driver._id}>
                  <td className="fs-16 fw-500">
                    {driver.driverName}
                  </td>
                  <td className="text-center fs-16 fw-bold">
                    Tsh {Method.getGeneralizedAmount(driver.pendingCashDeposit)}
                  </td>
                  {Method.hasPermission(OutstandingReport, View, currentUser) ? (
                    <td className="text-center fs-16 fw-bold">
                      <Button
                        variant="primary"
                        className="uw-btn uw-btn-primary fs-14 fw-600"
                        onClick={() => handleViewDetails(driver)}
                      >
                        View details
                      </Button>
                    </td>
                  ) : (
                    <></>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
    </Modal>
    </>
  );
};

export default PendingDepositModal;
